import React, { useCallback, useEffect, useState } from "react"
import { useAVToggle, usePreviewJoin } from "@100mslive/react-sdk"
import useEmailSubscription from "hooks/useEmailSubscription"
import { useWebinarAppContext } from "components/webinar/state"
import useTracking from "hooks/useTracking"
import { identifyUser } from "utils/identifyUser"
import PreviewForm from "./preview-form"
import {
  defaultPreviewPreference,
  UserPreferencesKeys,
  useUserPreferences,
} from "../hooks/useUserPreferences"
import { Title } from "../shared/text"
import { Container } from './styles'

const PreviewJoin = ({ token, onJoin, skipPreview, initialName, asRole, title, trackingId }) => {
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference,
  )

  const { emailList, emailSource, email: emailFrom } = useWebinarAppContext()
  const [name, setName] = useState(initialName || previewPreference.name)
  const [email, setEmail] = useState(emailFrom)
  const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle()
  const [previewError, setPreviewError] = useState(false)
  const [subscribing, subscribed, errEmail, subscribeEmail] = useEmailSubscription(trackingId)

  const { enableJoin, preview, join } = usePreviewJoin({
    name,
    token,
    initEndpoint: `https://prod-init.100ms.live/init`,
    initialSettings: {
      isAudioMuted: skipPreview || previewPreference.isAudioMuted,
      isVideoMuted: skipPreview || previewPreference.isVideoMuted,
      speakerAutoSelectionBlacklist: ["Yeti Stereo Microphone"],
    },
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        setPreviewError(true)
      }
    },
    asRole,
  })
  const { track } = useTracking()
  const savePreferenceAndJoin = useCallback(async () => {
    if (email) {
      subscribeEmail(email, emailList, emailSource)
    }
    setPreviewPreference({
      name,
      isAudioMuted: !isLocalAudioEnabled,
      isVideoMuted: !isLocalVideoEnabled,
    })
    join()
    if (onJoin) {
      onJoin()
      track("Webinar Registered", { webinarId: trackingId })
      try {
        await identifyUser({ user: { email } })
      } catch (error) {
        return error
      }
    }
  }, [
    email,
    setPreviewPreference,
    name,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    join,
    onJoin,
    subscribeEmail,
    emailList,
    emailSource,
  ])

  useEffect(() => {
    if (token) {
      if (skipPreview) {
        savePreferenceAndJoin()
      } else {
        preview()
      }
    }
  }, [token, skipPreview])

  return (
    <Container>
      <Title dangerouslySetInnerHTML={{ __html: title?.childMarkdownRemark.html }} />
      <PreviewForm
        name={name || ""}
        email={email || ""}
        onChangeName={setName}
        onChangeEmail={setEmail}
        enableJoin={enableJoin}
        onJoin={savePreferenceAndJoin}
      />
    </Container>
  )
}

export default PreviewJoin
